<template>
  <div class="w-full">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <LoadingComponent />
    </div>

    <!-- Card Principal -->
    <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 class="text-2xl font-bold text-gray-900">📋 Palpites Salvos</h2>
            <p class="text-sm mt-1 text-gray-600">Veja quem já registrou seus palpites</p>
          </div>
        </div>
      </div>

      <!-- Navegação de Rodada -->
      <div class="px-6 py-3 bg-gray-50 border-b border-gray-200">
        <div class="flex items-center justify-between max-w-md mx-auto">
          <button
            v-if="this.currentRoundId != 'Regular_Season_-_1'"
            @click="getRoundId(this.currentRoundId, 'prev')"
            class="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-green-50 hover:text-green-700 hover:border-green-300 transition-all duration-200 shadow-sm">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div v-else class="w-10"></div>

          <span class="text-sm font-bold text-gray-800 px-4">
            {{ formatTitle(this.currentRoundId) }}
          </span>

          <button
            @click="getRoundId(this.currentRoundId, 'next')"
            class="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-green-50 hover:text-green-700 hover:border-green-300 transition-all duration-200 shadow-sm">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- Legenda -->
      <div class="px-6 py-3 bg-gray-50 border-b text-xs flex flex-wrap gap-4">
        <div class="flex items-center">
          <div class="w-3 h-3 bg-green-100 border border-green-300 rounded mr-1"></div>
          <span>Palpite enviado</span>
        </div>
        <div class="flex items-center">
          <div class="w-3 h-3 bg-amber-100 border border-amber-300 rounded mr-1"></div>
          <span>Palpite pendente</span>
        </div>
        <div class="flex items-center">
          <div class="w-3 h-3 bg-red-100 border border-red-300 rounded mr-1"></div>
          <span>Não palpitou (criado automaticamente)</span>
        </div>
      </div>

      <!-- Tabela Desktop -->
      <div class="hidden lg:block overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">#</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Participante</th>
              <th class="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Status</th>
              <th class="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(user, index) in ranking"
              :key="user._id"
              :class="[
                'border-b hover:bg-gray-50 transition-colors',
                getStatus(user).row
              ]">
              <td class="px-6 py-3">
                <span class="font-bold text-gray-500">{{ index + 1 }}</span>
              </td>
              <td class="px-6 py-3">
                <span class="font-medium text-gray-900">{{ user.name }}</span>
              </td>
              <td class="px-6 py-3 text-center">
                <span
                  :class="[
                    'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold',
                    getStatus(user).badge
                  ]">
                  <i :class="[getStatus(user).icon, 'mr-1']"></i>
                  {{ getStatus(user).label }}
                </span>
              </td>
              <td class="px-6 py-3 text-center">
                <router-link
                  :to="{ name: 'NewBetting', params: { userId: user._id } }"
                  class="text-gray-600 hover:text-green-600 transition-colors"
                  title="Ver palpite">
                  <i class="far fa-edit text-lg"></i>
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards Mobile -->
      <div class="lg:hidden">
        <div
          v-for="(user, index) in ranking"
          :key="user._id"
          :class="[
            'border-b hover:bg-gray-50 transition-colors',
            getStatus(user).row
          ]">
          <router-link
            :to="{ name: 'NewBetting', params: { userId: user._id } }"
            class="flex items-center justify-between py-3 px-4">
            <!-- Posição -->
            <div class="flex items-center justify-center w-8">
              <span class="font-bold text-gray-500">{{ index + 1 }}</span>
            </div>

            <!-- Nome -->
            <div class="flex-1 min-w-0 mx-3">
              <span class="font-medium text-gray-900">{{ user.name }}</span>
            </div>

            <!-- Status -->
            <span
              :class="[
                'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold',
                getStatus(user).badgeMobile
              ]">
              <i :class="[getStatus(user).iconMobile, 'mr-1']"></i>
              {{ getStatus(user).label }}
            </span>
          </router-link>
        </div>
      </div>

      <!-- Rodapé com resumo -->
      <div class="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 flex justify-between">
        <span>Total: {{ ranking.length }} participantes</span>
        <span class="flex gap-3">
          <span class="text-green-700 font-semibold">{{ sentCount }} enviados</span>
          <span v-if="notBetCount > 0" class="text-red-700 font-semibold">
            {{ notBetCount }} não palpitaram
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import BetService from '@/services/betService';
import ApiService from '@/services/apiService';
import LoadingComponent from '@/components/LoadingComponent.vue';
import { useToast } from "vue-toastification";

// Os tres estados nunca convivem: antes da rodada iniciar existem SENT/PENDING,
// depois existem SENT/NOT_BET. Centralizado aqui para nao repetir a decisao de
// cor e rotulo nos blocos desktop e mobile do template.
const BET_STATUS = {
  SENT: {
    label: 'Enviado',
    row: 'border-l-4 border-green-500 bg-green-50',
    badge: 'bg-green-100 text-green-800',
    badgeMobile: 'bg-green-700 text-white',
    icon: 'fas fa-check-circle',
    iconMobile: 'fas fa-check',
    bg: 'bg-green-100',
  },
  PENDING: {
    label: 'Pendente',
    row: 'border-l-4 border-amber-500 bg-amber-50',
    badge: 'bg-amber-100 text-amber-800',
    badgeMobile: 'bg-amber-600 text-white',
    icon: 'fas fa-clock',
    iconMobile: 'fas fa-clock',
    bg: 'bg-amber-100',
  },
  NOT_BET: {
    label: 'Não palpitou',
    row: 'border-l-4 border-red-500 bg-red-50',
    badge: 'bg-red-100 text-red-800',
    badgeMobile: 'bg-red-600 text-white',
    icon: 'fas fa-times-circle',
    iconMobile: 'fas fa-times',
    bg: 'bg-red-100',
  },
};

export default {
  name: 'BettingSavedTable',
  components: {
    LoadingComponent,
  },
  props: [
    'userData',
    'roundId'
  ],
  setup() {
      const toast = useToast();
      return { toast }
  },
  computed: {
    sentCount() {
      return this.countByStatus(BET_STATUS.SENT);
    },
    notBetCount() {
      return this.countByStatus(BET_STATUS.NOT_BET);
    },
  },
  created() {
    this.$watch(
      () => this.$route.params,
      (toParams, previousParams) => {
        console.log(toParams, previousParams);
        this.loading = true;
        this.getData(toParams.roundId);
      }
    )
  },
  mounted() {
    this.getData(this.roundId);
  },
  methods: {
    async getData(roundId) {
      try {
        if (!roundId) {
          const currentRound = await ApiService.getCurrentRound();
          roundId = currentRound[0]._id;
        }

        // Definido antes da busca: se ela falhar, a navegação entre
        // rodadas continua funcionando em vez de quebrar com roundId undefined.
        this.currentRoundId = roundId;
        this.ranking = await BetService.getBettingSaved(roundId);
      } catch (err) {
        console.log(err);
        this.toast.error("Não foi possível carregar os palpites salvos.");
      } finally {
        // Fora do caminho feliz o spinner precisa sair de qualquer jeito.
        this.loading = false;
      }
    },
    getRoundId(roundId, type) {
      let round = roundId.split("_");
      let roundNumber = round.pop();
      roundNumber = parseInt(roundNumber);

      if (type === "prev") {
        let prevNumber = roundNumber - 1;
        round.push(prevNumber + '');
        roundId = round.join("_");
        return this.$router.push({
          name: "Betting Saved",
          params: { roundId: roundId }
        })
      }

      if (type === "next") {
        let prevNumber = roundNumber + 1;
        round.push(prevNumber + '');
        roundId = round.join("_");
        return this.$router.push({
          name: "Betting Saved",
          params: { roundId: roundId }
        })
      }
    },
    formatTitle(title) {
      if (title !== undefined) {
        return title.replaceAll("_", " ");
      }
      return title;
    },
    hasPermission(permission) {
      if (this.userData.length == 0) {
        return false;
      }
      let { rules } = this.userData;
      return rules.filter(data => {
        return data === permission || data === "SuperAdmin"
      }).length > 0;
    },
    getStatus(user) {
      if (!user.bet) {
        return BET_STATUS.PENDING;
      }

      // Checagem positiva: palpite de rodada antiga não tem o campo e
      // não pode ser confundido com criação automática.
      if (user.autoGenerated === true) {
        return BET_STATUS.NOT_BET;
      }

      return BET_STATUS.SENT;
    },
    countByStatus(status) {
      return this.ranking.filter(user => this.getStatus(user) === status).length;
    },
    getBgRows(user) {
      return this.getStatus(user).bg;
    },
  },
  data(){
    return {
      ranking: [],
      loading: true,
      currentRoundId: undefined,
    }
  },
}
</script>

<style scoped>
tr:hover {
  transform: scale(1.001);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>